import { AuthApiS, AuthSchemaS } from '@/features/Auth/outbound/share';
import {
  CertificationConstantS,
  CertificationErrorS,
  CertificationValidationS,
} from '@/features/Certification/outbound/share';
import type { ErrorJson } from '@/libs/errors';
import { isFeatureError } from '@/libs/errors/FeatureError';
import { T } from '@/libs/translation';
import { replaceBrToSpace } from '@/libs/util/replaceBrToSpace';
import {
  Button,
  type ButtonOption,
  Input,
  InputChangeEvent,
  InputTimerEndEvent,
  Label,
  Typo,
} from '@/ui/component/atom';
import { ConfirmToggleActiveEvent, ModalBodyView, Toast } from '@/ui/component/cell';
import { ChangeEmailEvent } from '@/ui/component/event';
import { pipe } from '@fxts/core';
import { mauth } from '@marpple/auth';
import { isAxiosError } from 'axios';
import { html } from 'rune-ts';
import klass from '../Common/Modal.module.scss';
import { makeErrorPopup } from '../Popup/makeErrorPopup';
import { makePopup } from '../Popup/makePopup';

export interface ChangeEmailModalData {
  email: string;
}

export class ChangeEmailModalBodyView extends ModalBodyView<ChangeEmailModalData> {
  component: {
    email: Input;
    new_email: Input;
    send_certification_code_button: Button;
    certification_code: Input;
  };

  constructor(data: ChangeEmailModalData) {
    super(data);

    this.component = {
      email: new Input(
        { value: data.email, id: 'change_email_modal_email' },
        {
          attr: { type: 'text', name: 'email', placeholder: '', readonly: true },
        },
      ),
      new_email: new Input(
        { value: '', id: 'change_email_modal_new_email' },
        {
          attr: {
            type: 'text',
            name: 'new_email',
            placeholder: T('mypage.change_email.placeholder.001'),
            required: true,
            autocomplete: 'off',
          },
          helper: {
            validators: [
              {
                message: T('mypage.change_email.validation.001'),
                validator: (value: string) => value !== data.email,
              },
            ],
          },
        },
      ),
      send_certification_code_button: new Button(
        {
          label: new Typo({ text: T('mypage.change_email.button.001') }, { typo: 'p2', weight: 'bold' }),
        },
        { type: 'button', size: 'small', color: 'disabled', disabled: true },
      ),
      certification_code: new Input(
        {},
        {
          delete_pattern: /\D/g,
          attr: {
            name: 'code',
            type: 'text',
            placeholder: T('mypage.change_email.placeholder.002'),
            maxlength: 6,
            required: true,
            inputmode: 'numeric',
            autocomplete: 'off',
          },
          helper: {
            validators: [
              {
                message: T('mypage.change_email.validation.002'),
              },
            ],
          },
          controller: {
            type: 'timer',
            timer: {
              minutes: 8,
              autoplay: false,
            },
          },
          klass: `${klass.code} ${klass.disabled}`,
        },
      ),
    };
  }

  private changeSendCertificationCodeButtonState({
    active,
    color,
    label,
  }: {
    active: boolean;
    color?: ButtonOption['color'];
    label?: Typo;
  }) {
    if (label) {
      this.component.send_certification_code_button.data.label = label;
    }

    this.component.send_certification_code_button.option.disabled = !active;
    this.component.send_certification_code_button.option.color = color ?? (active ? 'primary' : 'disabled');
    this.component.send_certification_code_button.redraw();
  }

  protected override template() {
    return html`<div class="${klass.modal_body}">
      <div class="${klass.field}">
        <div>
          ${new Label(
            {
              label: T('mypage.change_email.label.001'),
            },
            { required: true, for_id: 'change_email_modal_email' },
          )}
        </div>
        <div>${this.component.email}</div>
      </div>
      <div class="${klass.field}">
        <div>
          ${new Label(
            { label: T('mypage.change_email.label.002') },
            { required: true, for_id: 'change_email_modal_new_email' },
          )}
        </div>
        <div class="${klass.input_group}">
          ${this.component.new_email} ${this.component.send_certification_code_button}
        </div>
        ${this.component.certification_code}
      </div>
    </div>`;
  }

  override async confirm() {
    try {
      const email = this.component.new_email.inputBaseView.getValue();
      await pipe(
        {
          email,
          code: this.component.certification_code.inputBaseView.getValue(),
        },
        AuthSchemaS.ChangeEmailControllerReqBody.parse,
        AuthApiS.postChangeEmail,
      );

      await mauth.refreshUser();
      this.dispatchEvent(ChangeEmailEvent, { bubbles: true, detail: { email } });
      return true;
    } catch (e) {
      if (isFeatureError(e)) {
        await makeErrorPopup(e, e.json);
        return false;
      }

      if (isAxiosError<ErrorJson>(e)) {
        await makeErrorPopup(e, e.response?.data);
        return false;
      }

      await makeErrorPopup(e);
      return false;
    }
  }

  override close() {
    return true;
  }

  async timerEndHandler() {
    const res = await makePopup({
      title: T('mypage.change_email.code_timer_done'),
      message: T('mypage.change_email.code_timer_done_description'),
      ok: T('mypage.change_email.button.005'),
      cancel: T('common.cancel'),
    });

    const modal = this.getModal();
    modal.button.confirm.disabled();

    if (res === 'confirm') {
      return this.requestCodeHandler();
    }

    return;
  }

  private async requestCodeHandler() {
    try {
      await pipe(
        {
          email: this.component.new_email.inputBaseView.getValue(),
          certification_type: CertificationConstantS.CERTIFICATION_TYPE.CHANGE_EMAIL,
        },
        CertificationValidationS.validateCreateMemberEmailCertificationCheckEmailReqBody,
        AuthApiS.postEmailCertificationChangeEmail,
      );

      this.changeSendCertificationCodeButtonState({
        active: true,
        color: 'secondary',
        label: new Typo({ text: T('mypage.change_email.button.003') }, { typo: 'p2' }),
      });

      this.component.certification_code.option.klass = `${klass.code} ${klass.fade_in}`;
      this.component.certification_code.redraw();

      this.component.certification_code.inputControllerView.restart();
      this.component.certification_code.inputBaseView.setValue('');
      this.component.certification_code.enabled();
      this.component.certification_code.focus();

      Toast.open({ label: T('common.send_email_with_certification_code'), close_label: T('common.close') }, {});
    } catch (e) {
      if (isFeatureError(e)) {
        await makeErrorPopup(e, e.json);
      }

      if (isAxiosError(e)) {
        await makeErrorPopup(e, e.response?.data);
      } else {
        await makeErrorPopup(e);
      }

      this.component.certification_code.option.klass = `${klass.code} ${klass.fade_out}`;
      this.component.certification_code.inputControllerView.stop();
      this.component.certification_code.redraw();
    }
  }

  override onRender() {
    this.delegate(InputTimerEndEvent, Input, this.timerEndHandler);

    this.delegate(InputChangeEvent, Input, (e, input) => {
      if (input.option.attr?.name === 'new_email') {
        const new_email = this.component.new_email.value;
        const EMAIL_REGEX = /^([A-Z0-9_+-]+\.?)*[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;

        const active = new_email.length > 0 && EMAIL_REGEX.test(new_email) && new_email !== this.data.email;
        this.changeSendCertificationCodeButtonState({
          active,
          label: new Typo({ text: T('mypage.change_email.button.001') }, { typo: 'p2', weight: 'bold' }),
        });
      }
    });

    this.component.certification_code.inputBaseView.addEventListener('input', async (e) => {
      try {
        const input = e.target as HTMLInputElement;
        if (input.value.length === 6) {
          await pipe(
            {
              email: this.component.new_email.inputBaseView.getValue(),
              code: input.value,
              certification_type: CertificationConstantS.CERTIFICATION_TYPE.CHANGE_EMAIL,
            },
            CertificationValidationS.validateVerifyMemberEmailCertificationReqBody,
            AuthApiS.postEmailCertificationVerify,
          );

          // 인증 완료 처리
          this.changeSendCertificationCodeButtonState({
            active: false,
            color: 'disabled',
            label: new Typo({ text: T('mypage.change_email.button.002') }, { typo: 'p2', weight: 'bold' }),
          });

          this.component.new_email.option.attr!.readonly = true;
          this.component.new_email.redraw();

          this.component.certification_code.option.klass = `${klass.code} ${klass.fade_out}`;
          this.component.certification_code.inputControllerView.stop();
          this.component.certification_code.redraw();

          this.dispatchEvent(ConfirmToggleActiveEvent, { bubbles: true, detail: { active: true } });
        }
      } catch (e) {
        if (isFeatureError(e)) {
          return makeErrorPopup(e, e.json);
        }

        if (isAxiosError(e)) {
          const code = e?.response?.data.code;
          if (code === CertificationErrorS.CERTIFICATION_004.name) {
            this.component.certification_code.changeValidatorOnce({
              message: replaceBrToSpace(e.response?.data?.message ?? T('common.errors.title')),
              validator: (value: string) => false,
            });
          } else {
            await makeErrorPopup(e, e.response?.data);
          }
        } else {
          // console.error(e);
        }
      }
    });

    this.component.send_certification_code_button.addEventListener('click', this.requestCodeHandler.bind(this));
  }
}
